import React, { useMemo, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { ReportPageLayout } from "src/features/Reports/components/ReportPageLayout";
import { ReportDateRangePicker } from "src/features/Reports/components/ReportDateRangePicker";
import { ReportTabs } from "src/features/Reports/components/ReportTabs";
import { useReportQuery } from "src/features/Reports/hooks/useReportQuery";
import { getReportDateRange, type ReportDatePreset } from "src/features/Reports/utils/reportDateRange";
import type { ReportQueryInput, ReportsItemSalesData } from "src/features/Reports/types/report.types";
import { REPORTS_COMBO_SELECTION_SALES, REPORTS_ITEM_SALES } from "src/constants/queries";
import SalesByCategoryReportPage from "./SalesByCategoryReportPage";
import MenuPerformanceByItemTab from "./MenuPerformanceByItemTab";

const MENU_PERFORMANCE_TABS = [
  { id: "byItem", label: "By Item" },
  { id: "quickServiceSelections", label: "Quick Service Selections" },
  { id: "byCategory", label: "By Category" },
];

export default function MenuPerformanceReportPage() {
  const { hasRestaurant } = useAuth();
  const [datePreset, setDatePreset] = useState<ReportDatePreset>("last7");
  const [customFrom, setCustomFrom] = useState<string | null>(null);
  const [customTo, setCustomTo] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("byItem");

  const range = useMemo(() => {
    if (datePreset === "custom" && customFrom && customTo) {
      return { from: customFrom, to: customTo };
    }
    const r = getReportDateRange(datePreset);
    return { from: r.from, to: r.to };
  }, [datePreset, customFrom, customTo]);

  const handleDatePresetChange = (preset: ReportDatePreset, from: string, to: string) => {
    setDatePreset(preset);
    if (preset === "custom") {
      setCustomFrom(from);
      setCustomTo(to);
    } else {
      setCustomFrom(null);
      setCustomTo(null);
    }
  };

  const input: ReportQueryInput | null = useMemo(
    () =>
      hasRestaurant?.correlationId
        ? { storeCorrelationId: hasRestaurant.correlationId, from: range.from, to: range.to }
        : null,
    [hasRestaurant?.correlationId, range.from, range.to]
  );

  const { data: itemData, loading: itemLoading, error: itemError } = useReportQuery<ReportsItemSalesData>(
    REPORTS_ITEM_SALES,
    "reportsItemSales",
    input
  );
  const { data: comboSelectionData, loading: comboSelectionLoading, error: comboSelectionError } = useReportQuery<ReportsItemSalesData>(
    REPORTS_COMBO_SELECTION_SALES,
    "reportsComboSelectionSales",
    input
  );

  const tabContent = () => {
    switch (activeTab) {
      case "byItem":
        return (
          <MenuPerformanceByItemTab
            data={itemData}
            loading={itemLoading}
            error={itemError}
            view="all"
          />
        );
      case "byCategory":
        return <SalesByCategoryReportPage embedded input={input} />;
      case "quickServiceSelections":
        return (
          <MenuPerformanceByItemTab
            data={comboSelectionData}
            loading={comboSelectionLoading}
            error={comboSelectionError}
            view="all"
          />
        );
      default:
        return (
          <MenuPerformanceByItemTab
            data={itemData}
            loading={itemLoading}
            error={itemError}
            view="all"
          />
        );
    }
  };

  const toolbar = (
    <div className="flex w-full min-w-0 flex-col gap-2">
      <div className="flex w-full min-w-0 shrink-0 flex-wrap items-center justify-end gap-3">
        <ReportDateRangePicker
          value={datePreset}
          onChange={handleDatePresetChange}
          customFrom={datePreset === "custom" ? customFrom : undefined}
          customTo={datePreset === "custom" ? customTo : undefined}
        />
      </div>
      <ReportTabs
        tabs={MENU_PERFORMANCE_TABS}
        activeTabId={activeTab}
        onSelect={setActiveTab}
        inline
        inlineFullWidth
      />
    </div>
  );

  return (
    <ReportPageLayout hideTitle toolbar={toolbar} error={itemError}>
      {tabContent()}
    </ReportPageLayout>
  );
}
